import {
  Stack, Box, Typography, Tooltip, IconButton,
} from '@mui/material';
import React from 'react';
import TuneIcon from '@mui/icons-material/Tune';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import styled from '@emotion/styled';
import { toolTipText } from '../../../../utils/toolTipText';
import { customEntityKeys, eventSourceType } from '../../constants';
import palette from '../../../../theme/palette';

const EntityLabel = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 14,
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  lineHeight: 1.4,
}));

const EventLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  padding: '2px 6px',
  borderRadius: 4,
  backgroundColor: theme.palette.primary.lighter,
  color: theme.palette.primary.main,
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
}));

const IconBox = styled(IconButton)(() => ({
  borderRadius: 6,
  padding: 4,
  minWidth: 28,
  minHeight: 28,
}));

export default function CustomComponents({
  data, handleRefineFilter, handleRemove, type, customIcon,
}) {
  const parseOps = (item) => {
    if (Array.isArray(item)) return item;
    if (typeof item === 'string') {
      const parsed = JSON.parse(item);
      return Array.isArray(parsed) ? parsed : [];
    }
    return [];
  };

  const getSourceType = (sourceType) => {
    const defaultType = eventSourceType[0].name;
    if (!sourceType) {
      return defaultType;
    }
    const found = eventSourceType.find((e) => e.value === sourceType);
    return found.name || defaultType;
  };

  return (
    <Stack direction="row" flexWrap="wrap" mt={2} gap={1.5}>
      {data?.map((obj) => (
        <Box
          key={obj.custom_entity_id || obj.nested_feature_id}
          sx={{
            borderRadius: 1,
            border: `1px solid ${palette.grey[300]}`,
            bgcolor: 'background.paper',
            p: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            minWidth: 250,
            maxWidth: 400,
          }}
        >
          {/* Left: icon + name + events */}
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {React.cloneElement(customIcon, { fontSize: 'small' })}
              <Tooltip title={obj.custom_entity_label || obj.nested_feature_name}>
                <EntityLabel>
                  {obj.custom_entity_label || obj.nested_feature_name}
                </EntityLabel>
              </Tooltip>
            </Stack>

            {type === customEntityKeys.CUSTOM_OBJECT && parseOps(obj.operation_type).length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {parseOps(obj.operation_type).map((event) => (
                <EventLabel key={event}>{event}</EventLabel>
              ))}
            </Stack>
            )}

            {type !== customEntityKeys.NESTED_FEATURE && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <EventLabel>{getSourceType(obj.event_source_type?.[0])}</EventLabel>
            </Stack>
            )}
          </Stack>

          {/* Right: actions */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {type === customEntityKeys.CUSTOM_OBJECT && (
              <Tooltip title="Refine filter">
                <IconBox onClick={() => handleRefineFilter(obj)}>
                  <TuneIcon color="primary" fontSize="small" />
                </IconBox>
              </Tooltip>
            )}
            <Tooltip title={toolTipText.featureManager.REMOVE_CUSTOM_ENTITY}>
              <IconBox onClick={() => handleRemove(type, obj.custom_entity_id || obj.nested_feature_id)}>
                <RemoveCircleOutlineIcon color="primary" fontSize="small" />
              </IconBox>
            </Tooltip>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
